import React, { useEffect, useRef, useState } from 'react'

// scheme ของแอปปลายทางที่อนุญาตให้เปิดได้ (allowlist กัน open-redirect)
// หน้านี้เปิดสาธารณะบน domain หลัก ใครก็ยัด ?url= ได้ จึงจำกัดเฉพาะแอปธนาคาร/wallet
// ที่ WashXpress รองรับเท่านั้น เพิ่ม scheme ใหม่ที่ตัวแปรนี้ที่เดียว
const ALLOWED_SCHEMES = new Set([
  // --- scheme เดิมของหน้า next-launch ---
  'ktbnext', // Krungthai NEXT
  'maxmeapp', // MyMo / MAX me
  'bualuangmbanking', // Bualuang mBanking (BBL)
  // --- ธนาคาร/wallet ไทยที่พบบ่อย ---
  'scbeasy', // SCB EASY
  'kplus', // K PLUS (KBank)
  'ttbtouch', // ttb touch
  'krungsri', // Krungsri (KMA)
  'kmamobile', // Krungsri KMA (เผื่อ alias)
  'bay', // Krungsri (เผื่อ alias)
  'gsbmymo', // GSB MyMo
  'baac', // ธ.ก.ส.
  'uobtmrw', // UOB TMRW
  'truemoney', // TrueMoney Wallet
  'tmwallet', // TrueMoney (เผื่อ alias)
  'paotang', // เป๋าตัง (PaoTang)
  'make', // MAKE by KBank
  'maxme', // MAX me
])

// ชื่อแพ็กเกจ Android สำหรับ intent fallback ไป Play Store (เท่าที่ map ได้)
// ไม่มีใน map ก็ยังเปิดแอปได้ แค่ไม่มี fallback อัตโนมัติไป store
const ANDROID_PACKAGES: Record<string, string> = {
  ktbnext: 'com.ktb.customer.qr',
  bualuangmbanking: 'com.bbl.mobilebanking',
  scbeasy: 'com.scb.phone',
  kplus: 'com.kasikorn.retail.mbanking.wap',
  ttbtouch: 'com.ascendcorp.ewallet.touch',
  truemoney: 'th.co.truemoney.wallet',
  tmwallet: 'th.co.truemoney.wallet',
  paotang: 'com.ktb.customer.qr',
  make: 'com.kasikornbank.make',
}

const TIMEOUT_MS = 2500

type DeepLink = { url: string; scheme: string }

/** คืน scheme ที่ผ่าน allowlist แล้ว หรือ null ถ้าลิงก์ใช้ไม่ได้ */
export const resolveDeepLink = (rawUrl: string | null): DeepLink | null => {
  const url = (rawUrl || '').trim()
  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(url)?.[1]?.toLowerCase()
  if (!scheme || !ALLOWED_SCHEMES.has(scheme)) return null
  return { url, scheme }
}

const isAndroid = (): boolean => /android/i.test(navigator.userAgent)

/** in-app webview (FB / Messenger / LINE / IG) มักบล็อก custom scheme เงียบๆ */
export const isInAppBrowser = (ua: string = navigator.userAgent): boolean =>
  /FBAN|FBAV|FB_IAB|Messenger|Line\/|Instagram|MicroMessenger|TikTok/i.test(ua)

/**
 * Android: แปลง custom scheme เป็น intent:// เพื่อให้ Chrome fallback ไป Play Store
 * อัตโนมัติเมื่อไม่มีแอป (custom scheme ตรงๆ ไม่มี fallback ในตัว)
 */
const toAndroidIntentUrl = (
  link: DeepLink,
  fallbackUrl: string | null,
): string => {
  const rest = link.url.slice(link.scheme.length + 3) // ตัด "scheme://" ออก
  const pkg = ANDROID_PACKAGES[link.scheme]
  const fb =
    fallbackUrl ||
    (pkg ? `https://play.google.com/store/apps/details?id=${pkg}` : '')
  const parts = [`intent://${rest}#Intent`, `scheme=${link.scheme}`]
  if (pkg) parts.push(`package=${pkg}`)
  if (fb) parts.push(`S.browser_fallback_url=${encodeURIComponent(fb)}`)
  parts.push('end')
  return parts.join(';')
}

const NextLaunchPage: React.FC = () => {
  const [message, setMessage] = useState('กำลังเปิดแอป...')
  const [showSpinner, setShowSpinner] = useState(true)
  const [manualUrl, setManualUrl] = useState<string | null>(null)
  const [inAppWarning, setInAppWarning] = useState(false)

  const launchAttempted = useRef(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const link = resolveDeepLink(params.get('url'))
    const fallbackUrl = params.get('fallback')

    const showMessage = (text: string) => {
      setShowSpinner(false)
      setMessage(text)
    }

    if (!link) {
      showMessage('ลิงก์ไม่ถูกต้อง')
      return undefined
    }

    // in-app browser บล็อก scheme เงียบๆ — บอก user ให้เปิดใน browser จริง
    if (isInAppBrowser()) {
      setInAppWarning(true)
      setManualUrl(link.url)
      showMessage('กรุณาเปิดลิงก์นี้ใน Safari หรือ Chrome เพื่อเปิดแอป')
      return undefined
    }

    // ปิดแท็บ "เฉพาะตอนหน้านี้ถูกซ่อนจริง" = ระบบสลับไปเปิดแอปแล้ว
    //
    // ห้ามใช้ setTimeout ปิดแบบตายตัว: dialog ของ Chrome iOS
    // ("เว็บไซต์นี้พยายามเปิดแอปพลิเคชันอื่น") เป็น native alert ที่ซ้อนบนหน้าเว็บ
    // หน้าเว็บยัง visible อยู่ ดังนั้น timer จะปิดแท็บพร้อม dialog
    // ก่อนที่ผู้ใช้จะกด "อนุญาต" → แอปไม่ถูกเปิด
    //
    // เงื่อนไข hidden ทำให้รอ "อนุญาต" ได้เอง:
    //   กดอนุญาต   → แอปเปิด → หน้า hidden → ปิดแท็บ
    //   กดไม่อนุญาต → หน้ายัง visible → ไม่ปิด → ไป fallback / ค้างที่ข้อความ
    const onVisibilityChange = () => {
      if (launchAttempted.current && document.hidden) window.close()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    launchAttempted.current = true
    // Android ใช้ intent:// (มี fallback ในตัว), iOS/อื่นๆ ใช้ scheme ตรงๆ
    window.location.href = isAndroid()
      ? toAndroidIntentUrl(link, fallbackUrl)
      : link.url

    // กันค้างที่ spinner ถ้าไม่ได้ติดตั้งแอป หรือผู้ใช้กด "ไม่อนุญาต"
    // (Android intent:// จัดการ fallback เองแล้ว ส่วนนี้ครอบคลุม iOS/อื่นๆ)
    const onTimeout = () => {
      if (document.hidden) return // เปิดแอปสำเร็จแล้ว
      // ยังไม่มี focus = dialog ยังเด้งอยู่ → รอผู้ใช้ตอบก่อน ค่อยทำต่อ
      if (!document.hasFocus()) {
        window.addEventListener('focus', onTimeout, { once: true })
        return
      }
      if (fallbackUrl) {
        window.location.href = fallbackUrl // ไม่มีแอป → ไปปลายทางสำรอง
        return
      }
      setManualUrl(link.url)
      showMessage('ไม่สามารถเปิดแอปได้ กรุณาลองใหม่อีกครั้ง')
    }
    const timer = window.setTimeout(onTimeout, TIMEOUT_MS)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onTimeout)
      window.clearTimeout(timer)
    }
  }, [])

  return (
    <div
      style={{
        margin: 0,
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
        background: '#fff',
        color: '#333',
      }}
    >
      <div style={{ textAlign: 'center', padding: 24, maxWidth: 360 }}>
        {showSpinner && (
          <div
            style={{
              width: 36,
              height: 36,
              margin: '0 auto 16px',
              border: '3px solid #e5e5e5',
              borderTopColor: '#7b61ff',
              borderRadius: '50%',
              animation: 'next-launch-spin 0.8s linear infinite',
            }}
          />
        )}
        <p style={{ fontSize: 16, lineHeight: 1.5, margin: 0 }}>{message}</p>

        {inAppWarning && manualUrl && (
          <p
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              marginTop: 12,
              color: '#888',
            }}
          >
            แตะเมนู (•••) มุมขวาบน แล้วเลือก &ldquo;เปิดในเบราว์เซอร์&rdquo;
          </p>
        )}

        {!showSpinner && manualUrl && !inAppWarning && (
          <button
            type="button"
            onClick={() => {
              window.location.href = manualUrl
            }}
            style={{
              marginTop: 16,
              padding: '10px 24px',
              fontSize: 15,
              color: '#fff',
              background: '#7b61ff',
              border: 'none',
              borderRadius: 9999,
              cursor: 'pointer',
            }}
          >
            ลองเปิดแอปอีกครั้ง
          </button>
        )}
      </div>
      <style>
        {'@keyframes next-launch-spin { to { transform: rotate(360deg); } }'}
      </style>
    </div>
  )
}

export default NextLaunchPage
